import { makeAutoObservable, runInAction } from 'mobx';
import type { ResponseDataRow } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { cmApi } from '@src/api';
import { preloaderStore } from '@stores/preloaderStore';
import type { CmApi, ControlVersionDto } from '@src/api';

import type { ControlDtoDefId } from '../../controlsStore/controlsStore';

export type ControlVersionDtoDefId = Omit<ControlVersionDto, 'id'> & {
  id: string;
  repGroupName?: string;
  ctrlName?: string;
};

export type Filters = {
  reportGroup?: string;
  reportType?: string;
};

class ControlVersionsStore {
  controlVersions: ControlVersionDtoDefId[] = [];

  api: CmApi;

  filters: Filters = {};

  breadcrumbs: string[] = [];

  fullSize = false;

  selectNode: ControlVersionDtoDefId | null = null;

  selectedKeys: string[] = [];

  expandedKeys: string[] = [];

  checked: ResponseDataRow[] = [];

  isChanged = false; // флаг, что справочник изменился для проверки загрузки в fetchReferences

  constructor({ api }: { api: CmApi }) {
    this.api = api;
    makeAutoObservable(this);
  }

  setIsChanged = (value: boolean) => {
    this.isChanged = value;
  };

  setFilters = (filter: Filters) => {
    this.filters = filter;
  };

  setBreadcrumbs = (value: string[]) => {
    this.breadcrumbs = value;
  };

  toggleFullSize = () => {
    this.fullSize = !this.fullSize;
  };

  setSelectNode = (value: ControlDtoDefId | null) => {
    this.selectNode = value;

    if (value === null) {
      this.fullSize = false;
    }
  };

  setSelectedKeys = (value: string[]) => {
    this.selectedKeys = value;
  };

  setExpandedKeys = (value: string[]) => {
    this.expandedKeys = value;
  };

  setChecked = (value) => {
    this.checked = value;
  };

  fetch = async (): Promise<void> => {
    preloaderStore.preloader(true);
    const { data } = await this.api.controlVersions.getControlVersions();

    runInAction(() => {
      this.controlVersions = data?.map((item) => ({ ...item, id: item.ctrlVersCode ?? '' })) ?? [];
      this.isChanged = false;
    });
    preloaderStore.preloader(false);
  };

  groupDelete = async (controlVersions: ControlVersionDtoDefId[]) => {
    preloaderStore.preloader(true, 'удаление...');
    const promises = controlVersions.map((item) =>
      this.api.controlVersions.deleteControlVersionByCtrlVersCode(item.ctrlVersCode ?? ''),
    );
    await Promise.all(promises);
    await this.fetch();
    preloaderStore.preloader(false);
  };

  clear = () => {
    this.filters = {};
    this.breadcrumbs = [];
    this.fullSize = false;
    this.selectNode = null;
    this.selectedKeys = [];
    this.expandedKeys = [];
    this.checked = [];
  };
}

export const controlVersionsStore = new ControlVersionsStore({ api: cmApi });
